import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, ChevronLeft, ChevronRight, Save, Send } from 'lucide-react';
import { PASOS, PCT_REFERIDO, bodasApi, mensajeError, slugPareja } from '../components/bodas/bodasUtils';
import VistaPreviaIframe from '../components/bodas/VistaPreviaIframe';
import { Panel } from '../components/bodas/admin/comunes';
import { PasoBeneficio, PasoColores, PasoEvento, PasoPareja, PasoRevision } from '../components/bodas/admin/PasosBoda';
import { useVistaFoto } from '../components/bodas/admin/FotoCompartir';

const VACIA = {
  persona_a: '', persona_b: '', observaciones_internas: '', fecha_texto: '', codigo_vestuario: '', indicaciones_vestuario: '', colores: [],
  beneficio_tipo: 'porcentaje', beneficio_valor: String(PCT_REFERIDO), beneficio_aplica: '', beneficio_condiciones: '', beneficio_vigencia: '',
  foto_encuadre: null,
};
const CAMPOS = Object.keys(VACIA);

const Stepper = ({ paso, setPaso, puedeAvanzar }) => (
  <ol className="flex gap-1 overflow-x-auto pb-1" data-testid="boda-stepper">
    {PASOS.map((p, i) => (
      <li key={p}>
        <button type="button" onClick={() => (i <= paso || puedeAvanzar) && setPaso(i)} data-testid={`boda-paso-${i}`}
          className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${i === paso ? 'bg-slate-800 text-white' : i < paso ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-500'}`}>
          {i + 1}. {p}
        </button>
      </li>
    ))}
  </ol>
);

export default function EditorBoda() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [f, setF] = useState(VACIA);
  const [cliente, setCliente] = useState(null);
  const [contacto, setContacto] = useState(null);
  const [paso, setPaso] = useState(0);
  const [guardando, setGuardando] = useState(false);
  const [foto, setFoto] = useState({ url: null, archivo: null, quitar: false });
  const vista = useVistaFoto(foto);
  const set = (campo) => (v) => setF(prev => ({ ...prev, [campo]: v }));
  const parejaLista = f.persona_a.trim() && f.persona_b.trim();

  useEffect(() => {
    bodasApi.config().then(setContacto).catch(() => setContacto({}));
    if (!slug) return;
    bodasApi.obtener(slug).then(b => {
      setF(Object.fromEntries(CAMPOS.map(k => [k, b[k] ?? VACIA[k]])));
      if (b.cliente_id) setCliente({ id: b.cliente_id, nombre: b.cliente_nombre });
      if (b.foto_compartir) setFoto({ url: bodasApi.urlFotoPareja(slug, b.foto_compartir.version), archivo: null, quitar: false });
    }).catch(e => { toast.error(mensajeError(e, 'No se encontró la boda')); navigate('/admin/bodas'); });
  }, [slug, navigate]);

  const payload = useMemo(() => ({
    variante: 'boda', contacto: contacto || {}, borrador: true, avisoBorrador: 'Vista previa · Así la verán tus invitados',
    datos: { ...f, persona_a: f.persona_a || 'Nombre', persona_b: f.persona_b || 'Nombre', slug: slug || slugPareja(f.persona_a, f.persona_b), foto_url: vista },
  }), [f, contacto, slug, vista]);

  const guardar = async (publicar) => {
    if (!parejaLista) { setPaso(0); toast.error('Escribe los nombres de la pareja'); return; }
    setGuardando(true);
    try {
      const campos = { ...f, cliente_id: cliente?.id || null };
      const r = slug ? await bodasApi.editar(slug, campos) : await bodasApi.crear(campos);
      if (foto.archivo) await bodasApi.subirFoto(r.slug, foto.archivo);
      else if (foto.quitar && foto.url) await bodasApi.quitarFoto(r.slug);
      if (publicar) await bodasApi.estado(r.slug, 'publicada');
      toast.success(publicar ? 'Página publicada' : 'Borrador guardado');
      navigate('/admin/bodas');
    } catch (e) {
      toast.error(mensajeError(e, 'No se pudo guardar la boda'));
    } finally {
      setGuardando(false);
    }
  };

  const contenido = [
    <PasoPareja f={f} set={set} cliente={cliente} setCliente={setCliente} vista={vista} setFoto={setFoto} />,
    <PasoEvento f={f} set={set} />,
    <PasoColores f={f} setColores={set('colores')} />,
    <PasoBeneficio f={f} set={set} />,
    <PasoRevision f={f} cliente={cliente} foto={foto} />,
  ][paso];
  const ultimo = paso === PASOS.length - 1;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="editor-boda-page">
      <header className="sticky top-0 z-10 bg-white shadow-sm">
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-4">
          <button onClick={() => navigate('/admin/bodas')} className="rounded-lg p-2 transition hover:bg-slate-100" aria-label="Volver" data-testid="editor-boda-volver"><ArrowLeft className="h-5 w-5" /></button>
          <h1 className="text-xl font-bold text-slate-800">{slug ? 'Editar boda' : 'Nueva boda'}</h1>
        </div>
      </header>
      <main className="mx-auto grid max-w-7xl grid-cols-1 gap-6 px-4 py-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <Panel className="space-y-5 p-5">
          <Stepper paso={paso} setPaso={setPaso} puedeAvanzar={parejaLista} />
          <div key={paso}>{contenido}</div>
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-4">
            <button type="button" onClick={() => setPaso(p => p - 1)} disabled={paso === 0} className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-40" data-testid="boda-anterior"><ChevronLeft className="h-4 w-4" />Atrás</button>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => guardar(false)} disabled={guardando} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50" data-testid="boda-guardar-borrador"><Save className="h-4 w-4" />Guardar borrador</button>
              {ultimo
                ? <button type="button" onClick={() => guardar(true)} disabled={guardando} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-700 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-800 disabled:opacity-50" data-testid="boda-publicar"><Send className="h-4 w-4" />Guardar y publicar</button>
                : <button type="button" onClick={() => (parejaLista ? setPaso(p => p + 1) : toast.error('Escribe los nombres de la pareja'))} className="inline-flex items-center gap-1 rounded-lg bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700" data-testid="boda-siguiente">Siguiente<ChevronRight className="h-4 w-4" /></button>}
            </div>
          </div>
        </Panel>
        <VistaPreviaIframe payload={payload} nota="La vista previa se actualiza mientras escribes. El formulario solo funciona en la página publicada." />
      </main>
    </div>
  );
}
